import {
    Badge,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    LinearProgress,
    Menu,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import CloseIcon from '@mui/icons-material/Close'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { enqueueSnackbar } from 'notistack'
import {
    BulkCompleteProgress,
    getActiveBulkCompletions,
    getBulkCompletionProgress,
    previewBulkCompletion,
    startBulkCompletion,
} from '../../../../services/bulkCompletionService'
import { fetchAvalibleOrgsAndTemplates } from '../../../../services/entityInstanceServices'

// The server dictates cadence via `pollAfterMs` (adapts to progress velocity so large/slow
// operations don't generate redundant calls). These are only client-side guards/fallbacks.
const POLL_FLOOR_MS = 5000 // never poll faster than the server cache TTL
const POLL_CEIL_MS = 30000 // fallback ceiling if the server sends no hint
const POLL_HIDDEN_MS = 30000 // slow cadence while the tab is hidden (Page Visibility)
const POLL_JITTER = 0.15 // ±15% so many viewers of the same org don't poll in lockstep
// Stop polling only after this many *consecutive* failures — a single transient
// network/gateway hiccup must not freeze the progress bar until a manual refresh.
const MAX_POLL_FAILURES = 5
const TEAL = '#00797a'

// Clamp + jitter the server's recommended delay before the next poll.
const nextPollDelay = (recommendedMs: number): number => {
    const base = Math.min(
        POLL_CEIL_MS,
        Math.max(POLL_FLOOR_MS, recommendedMs || POLL_FLOOR_MS),
    )
    const jitter = base * POLL_JITTER * (Math.random() * 2 - 1)
    return Math.round(base + jitter)
}

interface Props {
    orgId: string
    templateId: string
    selectedShipmentIds: string[]
    /** True when the selection includes at least one already-completed shipment;
     * disables "Complete Selected" for the whole selection (UBQ-5245). */
    hasCompletedSelection?: boolean
    /** Called after a batch is started or finishes, so the listing can refresh. */
    onChanged?: () => void
    /** Called after an explicit-selection completion finishes, so the listing can clear its row
     * selection (stale selections otherwise re-map onto other rows once the list re-filters). */
    onCompleted?: () => void
}

type Mode = 'selected' | 'byOrg'

interface OrgOption {
    orgId: string
    name: string
}

const BulkCompleteAction = ({
    orgId,
    templateId,
    selectedShipmentIds,
    hasCompletedSelection = false,
    onChanged,
    onCompleted,
}: Props): ReactElement => {
    const { t } = useTranslation()

    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
    const [mode, setMode] = useState<Mode | null>(null)
    const [orgPickerOpen, setOrgPickerOpen] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [acknowledged, setAcknowledged] = useState(false)
    const [starting, setStarting] = useState(false)

    // by-org picker state
    const [orgOptions, setOrgOptions] = useState<OrgOption[]>([])
    const [orgSearch, setOrgSearch] = useState('')
    const [orgLoading, setOrgLoading] = useState(false)
    const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([])
    const [previewTotal, setPreviewTotal] = useState<number | null>(null)

    // running-batch progress
    const [progress, setProgress] = useState<BulkCompleteProgress | null>(null)
    const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const dismissRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const pollFailuresRef = useRef(0)
    const batchIdRef = useRef<string | null>(null)
    // Whether the currently-running op was started from an explicit row selection (vs by-org), so we
    // can clear the listing's selection when it finishes.
    const explicitRunRef = useRef(false)
    // Stable indirection so the self-rescheduling timeout always calls the latest pollProgress
    // without re-creating the timer each render.
    const runPollRef = useRef<(batchId: string) => void>(() => {})

    // How long the "done" chip lingers before auto-dismissing.
    const DONE_DISMISS_MS = 5000

    const isRunning = progress?.status === 'RUNNING'
    const hasSelection = selectedShipmentIds.length > 0
    // "Complete Selected" is offered only for a selection of not-yet-completed
    // shipments; a single completed shipment in the selection disables it.
    const completeSelectedDisabled =
        !hasSelection || hasCompletedSelection || isRunning
    const completeSelectedTooltip =
        hasSelection && hasCompletedSelection && !isRunning
            ? t(
                  'One or more selected shipments are already completed. Deselect them to continue.',
              )
            : ''

    const stopPolling = useCallback(() => {
        batchIdRef.current = null
        if (pollRef.current) {
            clearTimeout(pollRef.current)
            pollRef.current = null
        }
        if (dismissRef.current) {
            clearTimeout(dismissRef.current)
            dismissRef.current = null
        }
    }, [])

    const dismissProgress = useCallback(() => {
        stopPolling()
        setProgress(null)
    }, [stopPolling])

    // Schedule the next poll. Cadence is server-driven (pollAfterMs), clamped + jittered; while the
    // tab is hidden we back off to a slow fixed cadence and let the visibilitychange handler snap
    // back on re-focus.
    const scheduleNext = useCallback(
        (batchId: string, recommendedMs: number) => {
            if (pollRef.current) clearTimeout(pollRef.current)
            if (batchIdRef.current !== batchId) return
            const delay =
                typeof document !== 'undefined' && document.hidden
                    ? POLL_HIDDEN_MS
                    : nextPollDelay(recommendedMs)
            pollRef.current = setTimeout(
                () => runPollRef.current?.(batchId),
                delay,
            )
        },
        [],
    )

    const pollProgress = useCallback(
        async (batchId: string) => {
            if (batchIdRef.current !== batchId) return
            try {
                const p = await getBulkCompletionProgress(batchId)
                pollFailuresRef.current = 0
                setProgress(p)
                if (p.status !== 'RUNNING') {
                    stopPolling()
                    onChanged?.()
                    // An explicit-selection op finished — clear the listing's row selection so it
                    // doesn't linger and re-map onto other rows after the list re-filters.
                    if (explicitRunRef.current) {
                        explicitRunRef.current = false
                        onCompleted?.()
                    }
                    if (p.status === 'COMPLETED') {
                        if (p.total > 0 && p.completed < p.total) {
                            enqueueSnackbar(
                                t(
                                    'Completed {{completed}} of {{total}} shipments',
                                    {
                                        completed: p.completed,
                                        total: p.total,
                                    },
                                ),
                                { variant: 'warning' },
                            )
                        } else {
                            enqueueSnackbar(
                                t('Shipment Completion Successful'),
                                { variant: 'success' },
                            )
                        }
                    } else if (p.status === 'FAILED') {
                        enqueueSnackbar(t('Shipment completion failed'), {
                            variant: 'error',
                        })
                    }
                    // Show the terminal chip briefly, then clear it so it doesn't stick around.
                    if (dismissRef.current) clearTimeout(dismissRef.current)
                    dismissRef.current = setTimeout(
                        () => setProgress(null),
                        DONE_DISMISS_MS,
                    )
                    return
                }
                scheduleNext(batchId, p.pollAfterMs)
            } catch {
                // A single transient hiccup must not freeze the bar. Keep polling (with a floor
                // delay) and only give up after several consecutive failures.
                pollFailuresRef.current += 1
                if (pollFailuresRef.current >= MAX_POLL_FAILURES) {
                    stopPolling()
                    return
                }
                scheduleNext(batchId, POLL_FLOOR_MS)
            }
        },
        [onChanged, onCompleted, stopPolling, scheduleNext, t],
    )

    useEffect(() => {
        runPollRef.current = pollProgress
    }, [pollProgress])

    const startPolling = useCallback(
        (batchId: string) => {
            stopPolling()
            pollFailuresRef.current = 0
            batchIdRef.current = batchId
            pollProgress(batchId)
        },
        [pollProgress, stopPolling],
    )

    // Re-attach to an active batch when the listing loads / org changes.
    useEffect(() => {
        let cancelled = false
        stopPolling()
        setProgress(null)
        if (!orgId) return
        getActiveBulkCompletions(orgId)
            .then((batches) => {
                if (cancelled || !batches?.length) return
                setProgress(batches[0])
                if (batches[0].status === 'RUNNING') {
                    startPolling(batches[0].batchId)
                }
            })
            .catch(() => undefined)
        return () => {
            cancelled = true
            stopPolling()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId])

    // When the tab regains focus, poll immediately so the bar snaps to the latest count instead of
    // waiting out the slow hidden-tab cadence.
    useEffect(() => {
        const onVisible = () => {
            if (!document.hidden && batchIdRef.current) {
                pollProgress(batchIdRef.current)
            }
        }
        document.addEventListener('visibilitychange', onVisible)
        return () => document.removeEventListener('visibilitychange', onVisible)
    }, [pollProgress])

    const openMenu = (e: React.MouseEvent<HTMLElement>) =>
        setMenuAnchor(e.currentTarget)
    const closeMenu = () => setMenuAnchor(null)

    const onChooseSelected = () => {
        closeMenu()
        setMode('selected')
        setAcknowledged(false)
        setConfirmOpen(true)
    }

    // Load the creatable-org list — the same set (and same `/entity/org/subTypes` endpoint) that
    // shipment creation offers, so the user can only complete shipments in orgs he can create in.
    // The list comes back in full, so search is filtered client-side (no pagination — mirrors the
    // create form).
    const loadOrgs = useCallback(async () => {
        setOrgLoading(true)
        const res = await fetchAvalibleOrgsAndTemplates(orgId, templateId)
        if (Array.isArray(res)) {
            setOrgOptions(res.map((o) => ({ orgId: o.id, name: o.name })))
        } else {
            setOrgOptions([])
            enqueueSnackbar(t('Failed to load organizations'), {
                variant: 'error',
            })
        }
        setOrgLoading(false)
    }, [orgId, templateId, t])

    const onChooseByOrg = () => {
        closeMenu()
        setMode('byOrg')
        setSelectedOrgIds([])
        setPreviewTotal(null)
        setOrgSearch('')
        setOrgPickerOpen(true)
        loadOrgs()
    }

    const toggleOrg = (id: string) =>
        setSelectedOrgIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        )

    // Client-side search over the (already-fetched) creatable-org list.
    const visibleOrgOptions = useMemo(() => {
        const search = orgSearch.trim().toLowerCase()
        return search
            ? orgOptions.filter((o) => o.name?.toLowerCase().includes(search))
            : orgOptions
    }, [orgOptions, orgSearch])

    const confirmCount =
        mode === 'selected' ? selectedShipmentIds.length : previewTotal ?? 0

    // Compute the consolidated eligible count ONCE, when leaving the picker — not per checkbox
    // toggle (which previously fired a count query on every click).
    const proceedFromOrgPicker = async () => {
        if (selectedOrgIds.length === 0) return
        setOrgLoading(true)
        try {
            const r = await previewBulkCompletion({
                orgId,
                scopeOrgIds: selectedOrgIds,
            })
            setPreviewTotal(r.total)
            setOrgPickerOpen(false)
            setAcknowledged(false)
            setConfirmOpen(true)
        } catch {
            enqueueSnackbar(t('Failed to load organizations'), {
                variant: 'error',
            })
        } finally {
            setOrgLoading(false)
        }
    }

    const doStart = async () => {
        setStarting(true)
        const isExplicit = mode === 'selected'
        try {
            const payload = isExplicit
                ? { orgId, shipmentIds: selectedShipmentIds }
                : { orgId, scopeOrgIds: selectedOrgIds }
            const res = await startBulkCompletion(payload)
            setConfirmOpen(false)
            if (!res.total || !res.batchId) {
                // Nothing eligible (e.g. every selected shipment is already completed) — no-op.
                enqueueSnackbar(t('Action completed'), { variant: 'success' })
                if (isExplicit) onCompleted?.() // clear the (already-done) selection
                return
            }
            enqueueSnackbar(t('Shipment completion started'), {
                variant: 'success',
            })
            explicitRunRef.current = isExplicit
            startPolling(res.batchId)
            onChanged?.()
        } catch {
            enqueueSnackbar(t('Failed to start shipment completion'), {
                variant: 'error',
            })
        } finally {
            setStarting(false)
        }
    }

    const pct = useMemo(() => {
        if (!progress || progress.total === 0) return 0
        return Math.min(
            100,
            Math.round((progress.completed / progress.total) * 100),
        )
    }, [progress])

    return (
        <>
            {/* Single compact launcher (⋮). A pulsing badge signals a running batch; progress lives
                inside the menu, so the toolbar keeps zero persistent real estate. */}
            <Tooltip title={t('Bulk Actions')}>
                <IconButton
                    id="bulk-actions-button"
                    onClick={openMenu}
                    aria-label={t('Bulk Actions')}
                    size="small"
                    sx={{
                        color: progress ? TEAL : '#6b7785',
                        '&:hover': { color: TEAL, background: 'transparent' },
                    }}
                >
                    <Badge
                        variant="dot"
                        invisible={!progress}
                        overlap="circular"
                        sx={{
                            '& .MuiBadge-badge': {
                                backgroundColor: isRunning ? '#c77700' : TEAL,
                                boxShadow: '0 0 0 2px #fff',
                            },
                        }}
                    >
                        <MoreVertIcon sx={{ fontSize: 20 }} />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={closeMenu}
                slotProps={{
                    paper: { sx: { width: 320, borderRadius: '12px' } },
                }}
            >
                <Typography
                    sx={{
                        px: 1.75,
                        pt: 1.25,
                        pb: 0.5,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '.4px',
                        textTransform: 'uppercase',
                        color: '#8a97a4',
                    }}
                >
                    {t('Bulk Actions')}
                </Typography>

                {/* Progress block (only while a batch is active / just finished) */}
                {progress && (
                    <Box
                        id="bulk-complete-progress"
                        sx={{
                            px: 1.75,
                            py: 1.25,
                            mx: 1,
                            mb: 0.5,
                            background: '#f2fafa',
                            border: '1px solid #e4eef0',
                            borderRadius: '8px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1,
                            }}
                        >
                            {isRunning ? (
                                <CircularProgress
                                    size={14}
                                    sx={{ color: TEAL }}
                                />
                            ) : (
                                <CheckCircleOutlineIcon
                                    sx={{ fontSize: 16, color: TEAL }}
                                />
                            )}
                            <Typography
                                sx={{
                                    fontSize: 12.5,
                                    fontWeight: 700,
                                    color: '#025b5c',
                                }}
                            >
                                {isRunning
                                    ? t('Completing Shipments')
                                    : t('Shipment Completion Done')}
                            </Typography>
                            {!isRunning && (
                                <IconButton
                                    size="small"
                                    onClick={dismissProgress}
                                    aria-label={t('Dismiss')}
                                    sx={{
                                        ml: 'auto',
                                        p: '2px',
                                        color: '#6b7785',
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            )}
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{
                                height: 6,
                                borderRadius: 999,
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: TEAL,
                                },
                            }}
                        />
                    </Box>
                )}

                {/* Tooltip wraps a span because a disabled MenuItem swallows hover
                    events; the reason only shows when the block is due to a
                    completed shipment being in the selection. */}
                <Tooltip
                    title={completeSelectedTooltip}
                    placement="left"
                    disableHoverListener={!completeSelectedTooltip}
                >
                    <span>
                        <MenuItem
                            id="bulk-complete-selected-menuitem"
                            onClick={onChooseSelected}
                            disabled={completeSelectedDisabled}
                        >
                            {hasSelection
                                ? t('Complete Selected ({{count}})', {
                                      count: selectedShipmentIds.length,
                                  })
                                : t('Complete Selected')}
                        </MenuItem>
                    </span>
                </Tooltip>
                <MenuItem
                    id="bulk-complete-by-org-menuitem"
                    onClick={onChooseByOrg}
                    disabled={isRunning}
                >
                    {t('Complete All — By Organization')}
                </MenuItem>
            </Menu>

            {/* Org picker */}
            <Dialog
                open={orgPickerOpen}
                onClose={() => setOrgPickerOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('Complete All — By Organization')}</DialogTitle>
                <DialogContent dividers>
                    <Typography sx={{ fontSize: 13, color: '#6b7785', mb: 1 }}>
                        {t(
                            'Pick one or more organizations. All eligible shipments in the selected organizations will be completed.',
                        )}
                    </Typography>
                    <TextField
                        id="bulk-complete-org-search"
                        size="small"
                        fullWidth
                        placeholder={t('Search Organizations')}
                        value={orgSearch}
                        onChange={(e) => setOrgSearch(e.target.value)}
                        sx={{ mb: 1 }}
                    />
                    {selectedOrgIds.length > 0 && (
                        <Typography
                            sx={{ fontSize: 12, color: '#025b5c', mb: 0.5 }}
                        >
                            {t('{{count}} Organizations Selected', {
                                count: selectedOrgIds.length,
                            })}
                        </Typography>
                    )}
                    <Box id="bulk-complete-org-list" sx={{ minHeight: 220 }}>
                        {orgLoading ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    py: 4,
                                }}
                            >
                                <CircularProgress
                                    size={22}
                                    sx={{ color: TEAL }}
                                />
                            </Box>
                        ) : visibleOrgOptions.length === 0 ? (
                            <Typography
                                sx={{ fontSize: 13, color: '#6b7785', py: 2 }}
                            >
                                {t('No Organizations Found')}
                            </Typography>
                        ) : (
                            visibleOrgOptions.map((o) => (
                                <FormControlLabel
                                    key={o.orgId}
                                    control={
                                        <Checkbox
                                            id={`bulk-complete-org-${o.orgId}-checkbox`}
                                            size="small"
                                            checked={selectedOrgIds.includes(
                                                o.orgId,
                                            )}
                                            onChange={() => toggleOrg(o.orgId)}
                                            sx={{
                                                '&.Mui-checked': {
                                                    color: TEAL,
                                                },
                                            }}
                                        />
                                    }
                                    label={o.name}
                                    sx={{ display: 'flex' }}
                                />
                            ))
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        id="bulk-complete-org-cancel"
                        onClick={() => setOrgPickerOpen(false)}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        id="bulk-complete-org-continue"
                        variant="contained"
                        onClick={proceedFromOrgPicker}
                        disabled={selectedOrgIds.length === 0 || orgLoading}
                        sx={{ background: TEAL }}
                    >
                        {t('Continue')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation */}
            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: '12px' } }}
            >
                <DialogTitle sx={{ fontSize: 20, fontWeight: 600 }}>
                    {t('Confirm Shipment Completion')}
                </DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    <Typography sx={{ fontSize: 16, mb: 2.5 }}>
                        {t('You are about to complete {{count}} shipments.', {
                            count: confirmCount,
                        })}
                    </Typography>
                    <Box
                        sx={{
                            background: '#fdf6ec',
                            border: '1px solid #f0d9a8',
                            borderRadius: '8px',
                            p: '14px 18px',
                            mb: 2,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 15,
                                fontWeight: 700,
                                color: '#8a5a00',
                                mb: 1,
                            }}
                        >
                            {t('What Completion Does')}
                        </Typography>
                        <Box
                            component="ul"
                            sx={{
                                m: 0,
                                pl: 2.5,
                                fontSize: 14,
                                color: '#7a5a1a',
                                lineHeight: 1.8,
                            }}
                        >
                            <li>
                                {t(
                                    'Sets each shipment status to Completed and closes the active trip.',
                                )}
                            </li>
                            <li>
                                {t(
                                    'Ends the device and maritime relationships, then archives the device. Multi-use devices remain available for reuse.',
                                )}
                            </li>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            background: '#eef8f8',
                            border: '1px solid #cfe8e8',
                            borderRadius: '8px',
                            p: '14px 18px',
                            mb: 2.5,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 15,
                                fontWeight: 700,
                                color: '#025b5c',
                                mb: 1,
                            }}
                        >
                            {t('Your Data Stays')}
                        </Typography>
                        <Box
                            component="ul"
                            sx={{
                                m: 0,
                                pl: 2.5,
                                fontSize: 14,
                                color: '#025b5c',
                                lineHeight: 1.8,
                            }}
                        >
                            <li>
                                {t(
                                    'The shipment remains fully accessible — listing, preview, reports and alerts are unchanged.',
                                )}
                            </li>
                            <li>
                                {t(
                                    'Sensor history and time-series is retained.',
                                )}
                            </li>
                        </Box>
                    </Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                id="bulk-complete-ack-checkbox"
                                checked={acknowledged}
                                onChange={(e) =>
                                    setAcknowledged(e.target.checked)
                                }
                                sx={{ '&.Mui-checked': { color: TEAL } }}
                            />
                        }
                        label={t(
                            'I understand this will complete {{count}} shipments and cannot be reverted.',
                            { count: confirmCount },
                        )}
                        sx={{
                            '& .MuiFormControlLabel-label': { fontSize: 14 },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: '12px 24px' }}>
                    <Button
                        id="bulk-complete-cancel"
                        onClick={() => setConfirmOpen(false)}
                        sx={{ fontSize: 14, textTransform: 'none' }}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        id="bulk-complete-start"
                        variant="contained"
                        onClick={doStart}
                        disabled={
                            !acknowledged || starting || confirmCount === 0
                        }
                        startIcon={
                            starting ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : undefined
                        }
                        sx={{
                            background: TEAL,
                            fontSize: 14,
                            textTransform: 'none',
                            '&:hover': { background: '#025b5c' },
                        }}
                    >
                        {t('Start Completion')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default BulkCompleteAction
